import { useCallback, useEffect, useRef, useState } from 'react';
import { NotaPush, type NativePushStatus } from '../lib/native.js';
import { errorMessage } from '../lib/errors.js';

// L'endpoint arrive du distributeur après enable(), puis le natif le fait confirmer par le
// serveur : la modale relit status() jusqu'à la confirmation, un refus, ou ce délai.
const POLL_MS = 1000;
const POLL_LIMIT = 20;

export interface UseNativePush {
  status: NativePushStatus | null;
  busy: boolean;
  /** Vrai entre enable() et la confirmation du serveur. */
  waiting: boolean;
  error: string | null;
  enable: () => Promise<void>;
  disable: () => Promise<void>;
  openSettings: () => void;
}

/** La ligne d'état de la modale dans la coque : mêmes libellés que le web push, plus « aucun distributeur ». */
export function nativeStatusLine(status: NativePushStatus | null, waiting: boolean, error: string | null): string {
  if (error) return error;
  if (!status) return 'Checking…';
  if (status.error) return status.error;
  if (!status.distributor) return 'No push service on this phone yet. Follow the steps above, then tap Enable.';
  if (status.permission === 'denied') return 'Blocked. Allow notifications for Nota in Android settings.';
  if (status.subscribed) return 'Notifications are on.';
  if (waiting) return 'Enabling…';
  if (status.permission === 'prompt') return 'Tap Enable to allow notifications.';
  return 'Notifications are off on this phone.';
}

/** Le push de la coque Android (spec 18). `open` : la modale est visible — l'état se relit à chaque ouverture. */
export function useNativePush(open: boolean): UseNativePush {
  const [status, setStatus] = useState<NativePushStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const [waiting, setWaiting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Chaque arrêt change de génération : un status() parti avant l'arrêt ne relance rien et
  // n'écrase pas l'état posé depuis (disable(), fermeture de la modale).
  const generation = useRef(0);

  const stopPolling = useCallback(() => {
    generation.current += 1;
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    setWaiting(false);
  }, []);

  const poll = useCallback(
    (left: number, gen: number) => {
      timer.current = setTimeout(() => {
        NotaPush.status()
          .then((s) => {
            if (gen !== generation.current) return;
            setStatus(s);
            if (s.subscribed || s.error || left <= 1) stopPolling();
            else poll(left - 1, gen);
          })
          .catch(() => {
            if (gen === generation.current) stopPolling();
          });
      }, POLL_MS);
    },
    [stopPolling]
  );

  useEffect(() => {
    if (!open) return stopPolling;
    NotaPush.status()
      .then(setStatus)
      .catch((err: unknown) => setError(errorMessage(err, 'Notifications unavailable')));
    return stopPolling;
  }, [open, stopPolling]);

  const enable = useCallback(async () => {
    stopPolling();
    setBusy(true);
    setError(null);
    try {
      const s = await NotaPush.enable();
      setStatus(s);
      if (s.distributor && s.permission === 'granted' && !s.subscribed && !s.error) {
        setWaiting(true);
        poll(POLL_LIMIT, generation.current);
      }
    } catch (err) {
      setError(errorMessage(err, 'Enable failed'));
    } finally {
      setBusy(false);
    }
  }, [poll, stopPolling]);

  const disable = useCallback(async () => {
    stopPolling();
    setBusy(true);
    setError(null);
    try {
      setStatus(await NotaPush.disable());
    } catch (err) {
      setError(errorMessage(err, 'Disable failed'));
    } finally {
      setBusy(false);
    }
  }, [stopPolling]);

  const openSettings = useCallback(() => {
    void NotaPush.openSettings().catch(() => {});
  }, []);

  return { status, busy, waiting, error, enable, disable, openSettings };
}
